/* Turning a reaction id into something to draw.

   The socket carries an **id and nothing else** — see the backend's `Reaction` for why: a server
   that sent the words would send English to a Spanish player, and would be one release away from
   being a channel for arbitrary strings. So the emoji comes from the catalogue in
   `GET /game-config/` and the line comes from this client's own translations, looked up by id with
   the server's English label as the fallback. That is the same split `errorMessages.js` uses for
   refusals, for the same reason.

   Everything here tolerates an unknown id, because it will happen: a server that has added a
   reaction this build has never heard of is the ordinary state of affairs for a few minutes after
   a deploy. An unknown one draws as a plain dot rather than as nothing, so a player still sees
   that somebody said *something*. */

import i18n from "src/i18n"
import { reactionImages } from "src/assets/images"

export const REACTION_SLOT = "reaction"

// How long one stays on screen, and how many may be there at once. Both are the *receiver's*
// decision on purpose: the server's cooldown is per connection and a patched client could ignore
// it, so the screen being filled is the thing that has to decide what it draws. Two per player
// means a spammer takes two slots and everybody else still gets through.
export const REACTION_LIFETIME_MS = 4000
export const MAX_REACTIONS_ON_SCREEN = 5
export const MAX_REACTIONS_PER_PLAYER = 2

export const reactionsIn = (cosmetics) => (cosmetics || []).filter((item) => item.slot === REACTION_SLOT)

export const reactionById = (cosmetics, id) => reactionsIn(cosmetics).find((item) => item.id === id) || null

/* The line under the emoji. `defaultValue` is the server's English, so a reaction added to the
   catalogue is sendable and readable before either locale file mentions it. */
export const reactionText = (reaction) =>
  reaction ? i18n.t(`reactions.${reaction.id}`, { defaultValue: reaction.label || "" }) : ""

export const reactionEmoji = (reaction) => reaction?.emoji || "•"

/* A reaction that is a picture, not a glyph. The catalogue says which (`picture`), and this client
   says *what* — an id with no file bundled falls back to the emoji path, so a server that has
   added one this build has never seen still draws something rather than a hole. */
export const reactionImage = (reaction) =>
  reaction?.picture ? reactionImages[reaction.id] || null : null

/* A picture reaction shows no words on the map: the meme is the whole message, and a caption under
   it would be explaining the joke. Its `label` still exists — the shop needs a name on the card —
   which is why this asks rather than reading `label` being empty. */
export const reactionShowsText = (reaction) => !reaction?.picture

/* The tray: every reaction this player may send, free ones first so the six that cost nothing are
   the ones under the thumb. `owned` comes from the wallet, and the server checks it again — this
   only decides which buttons to draw. */
export const sendableReactions = (cosmetics, owned) => {
  const held = new Set(owned || [])
  return reactionsIn(cosmetics)
    .filter((reaction) => reaction.free || held.has(reaction.id))
    .sort((a, b) => Number(Boolean(a.price)) - Number(Boolean(b.price)))
}

export default sendableReactions
